/**
 * OrigemDaEntrega — de onde a Conferência leu a entrega e o que cada botão exige.
 *
 * Produção, 2026-10-01 (V3, c9dd94d9): sem PR a página ficou vermelha e nada dizia que a
 * entrega pendente já basta para conferir e auditar; "Aceitar entrega e revisar commit"
 * levava ao Gate Final, que revisa o commit de um PR que não existia; e "Reconferir"
 * aceitava a execução em andamento. A regra fica aqui, pura, para a tela e os testes.
 */
import { C } from '@/theme/palette';
import { useNavigate } from 'react-router-dom';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

/**
 * O que a página pode fazer agora.
 *  - conferir/auditar: a entrega pendente basta (não precisa de PR); só não com a
 *    execução em andamento (conferiria uma entrega pela metade).
 *  - aceitar (Gate Final): revisa o commit do PR — exige a entrega enviada ao GitHub.
 *    Projeto de ANÁLISE não gera código nem PR: o aceite fica livre.
 */
export function estadoDaPagina(entrega, { analise = false } = {}) {
  const e = entrega || null;
  const emAndamento = Boolean(e?.emAndamento);
  const temPr = Boolean(e?.prUrl);
  const enviada = e?.status === 'pushed' || temPr;
  const id8 = String(e?.executionId || '').slice(0, 8);
  return {
    podeConferir: !emAndamento,
    motivoConferir: emAndamento
      ? `A execução ${id8} está em andamento — confira quando ela terminar.` : '',
    podeAceitar: analise || (!emAndamento && enviada),
    motivoAceitar: analise ? ''
      : emAndamento ? `A execução ${id8} ainda está em andamento.`
        : !enviada ? 'O Gate Final revisa o commit do Pull Request: envie a entrega ao GitHub '
          + '(tela Saída → "Enviar ao GitHub") e volte para aceitar.'
          : '',
    enviada,
    desatualizada: Boolean(e?.desatualizada),
  };
}

export function OrigemDaEntrega({ entrega, analise = false }) {
  const navigate = useNavigate();
  if (!entrega || analise) return null;
  const st = estadoDaPagina(entrega);
  const id8 = String(entrega.executionId || '').slice(0, 8);
  const irParaSaida = () => navigate(`/execution/output?execution=${entrega.executionId}`);
  const caixa = (cor, children) => (
    <div role="status" style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8,
      background: `${cor}12`, border: `1px solid ${cor}55`, color: C.text, fontSize: 12.5,
      lineHeight: 1.5, maxWidth: '86ch' }}>{children}</div>
  );
  return (
    <>
      {entrega.emAndamento && caixa(C.warn, (
        <>⏳ A execução <strong>{id8}</strong> está em andamento. Conferência e Auditoria ficam
          disponíveis quando ela terminar — antes disso elas comparariam uma entrega pela metade.</>
      ))}
      {!entrega.emAndamento && !st.enviada && caixa(C.cyber, (
        <>📦 Esta página lê a <strong>entrega pendente</strong> da execução <strong>{id8}</strong>,
          ainda <strong>não enviada ao GitHub</strong>. Conferir e auditar <strong>não precisam
          de Pull Request</strong>. O PR só é necessário para o <strong>Gate Final</strong>, que
          revisa o commit.{' '}
          <button type="button" onClick={irParaSaida} style={{ cursor: 'pointer',
            background: 'transparent', color: C.cyber, border: `1px solid ${C.cyber}66`,
            borderRadius: 6, padding: '2px 10px', fontSize: 12 }}>
            Ir para "Enviar ao GitHub" →
          </button></>
      ))}
      {!entrega.emAndamento && st.enviada && caixa(C.cyber, (
        <>🔗 Entrega enviada ao GitHub
          {entrega.prUrl ? (
            <> — <a href={entrega.prUrl} target="_blank" rel="noopener noreferrer"
              style={{ color: C.cyber }}>Pull Request{entrega.prNumber ? ` #${entrega.prNumber}` : ''}</a></>
          ) : null}. Conferência e Auditoria leem o que foi enviado.</>
      ))}
      {st.desatualizada && caixa(C.digital, (
        <>⚠ A matriz do QA abaixo é de uma <strong>execução anterior</strong>. Clique em
          {' '}<strong>Reconferir</strong> para conferir a execução {id8}.</>
      ))}
    </>
  );
}

export default OrigemDaEntrega;
